import { computed } from 'vue';
import { translate, useCaptionSettingsOptions } from '@/composables/useCaptionSettings';
import fontIcon from '@/assets/icons/sorenson/font-family.svg';

// Shared by the desktop/tablet (GreenScreenBar) and mobile (GreenScreenBarMobile) Green Screen
// top bars.

export const GREEN_SCREEN_BAR_PROPS = {
  fontSize: {
    type: Number,
    required: true,
  },
  minFontSize: Number,
  maxFontSize: Number,
  lines: {
    type: Number,
    required: true,
  },
  // 'narrow' | 'wide', the values of the shared caption width options.
  width: {
    type: String,
    required: true,
  },
  align: {
    type: String,
    required: true,
  },
  // Style key from Constants.CAPTION_FONTS, not a font name.
  font: {
    type: String,
    required: true,
  },
};

export const GREEN_SCREEN_BAR_EMITS = ['view', 'leave', 'shortcuts', 'step-font-size', 'set-font-size', 'lines', 'width', 'align', 'font', 'menu-state'];

export const GREEN_SCREEN_VIEWS = [
  { label: 'session_views_default', icon: '/svg/chat.svg', code: 'default' },
  { label: 'Green Screen', icon: '/svg/chromakey.svg', code: 'chromakey' },
];

export const CHECK_ICON_PATH = 'M17.2559 4.41074C17.5814 4.73618 17.5814 5.26382 17.2559 5.58926L8.08926 14.7559C7.76382 15.0814 7.23618 15.0814 6.91074 14.7559L2.74408 10.5893C2.41864 10.2638 2.41864 9.73618 2.74408 9.41074C3.06951 9.08531 3.59715 9.08531 3.92259 9.41074L7.5 12.9882L16.0774 4.41074C16.4028 4.08531 16.9305 4.08531 17.2559 4.41074Z';

// Line count steps through 1-3 and wraps back to 1.
export const nextLineCount = (lines) => (lines >= 3 ? 1 : lines + 1);

// The tile shows the icon of the option that is currently picked.
const selectedIcon = (options, value) => (options.find((option) => option.value === value) || options[0]).icon;

// showWidth: phones always get the wide captions, so the mobile bar leaves the Width menu out.
export function useGreenScreenBar(props, { showWidth = true } = {}) {
  const {
    sizeLabel,
    alignmentLabel,
    widthLabel,
    fontLabel,
    widthOptions,
    fontOptions,
    alignOptions,
  } = useCaptionSettingsOptions();

  const lineCountLabel = computed(() => translate('toolbar_line_count', 'Line count'));
  const backgroundLabel = computed(() => translate('toolbar_background', 'Background'));

  // Width, alignment and font: pick one of a few options.
  const choiceMenus = computed(() => [
    {
      key: 'width',
      label: widthLabel.value,
      icon: selectedIcon(widthOptions.value, props.width),
      value: props.width,
      options: widthOptions.value,
    },
    {
      key: 'align',
      label: alignmentLabel.value,
      icon: selectedIcon(alignOptions.value, props.align),
      value: props.align,
      options: alignOptions.value,
      optionIcons: true,
      alignRight: true,
    },
    {
      key: 'font',
      label: fontLabel.value,
      icon: fontIcon,
      value: props.font,
      options: fontOptions.value,
      alignRight: true,
    },
  ].filter((menu) => showWidth || menu.key !== 'width'));

  return {
    sizeLabel,
    lineCountLabel,
    backgroundLabel,
    choiceMenus,
  };
}
